import React from 'react';
import { AlertTriangle, Trash2 } from 'lucide-react';

/**
 * Reusable confirmation dialog component for destructive actions
 *
 * @param {boolean} isOpen - Whether the dialog is open
 * @param {Function} onConfirm - Callback when user confirms the action
 * @param {Function} onCancel - Callback when user cancels
 * @param {string} title - Dialog title (default: "Confirm Delete")
 * @param {string} message - Message to display
 * @param {string} confirmText - Text for confirm button (default: "Delete")
 * @param {string} cancelText - Text for cancel button (default: "Cancel")
 * @param {boolean} isLoading - Whether the action is in progress
 * @param {string} variant - "danger" (default) or "warning"
 */
const ConfirmationDialog = ({
  isOpen,
  onConfirm,
  onCancel,
  title = 'Confirm Delete',
  message = 'Are you sure you want to delete this item? This action cannot be undone.',
  confirmText = 'Delete',
  cancelText = 'Cancel',
  isLoading = false,
  variant = 'danger'
}) => {
  if (!isOpen) return null;

  const isDanger = variant === 'danger';
  const Icon = isDanger ? Trash2 : AlertTriangle;
  const iconBgClass = isDanger ? 'bg-red-500/20' : 'bg-yellow-500/20';
  const iconTextClass = isDanger ? 'text-red-400' : 'text-yellow-400';
  const confirmBtnClass = isDanger
    ? 'bg-red-500/20 border-red-500/30 text-red-400 hover:bg-red-500/30'
    : 'bg-yellow-500/20 border-yellow-500/30 text-yellow-400 hover:bg-yellow-500/30';

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm"
      data-testid="confirmation-dialog"
      onClick={(e) => {
        // Close on backdrop click (only if not loading)
        if (e.target === e.currentTarget && !isLoading) {
          onCancel();
        }
      }}
    >
      <div
        className="glass border border-light-border dark:border-border rounded-2xl p-6 max-w-md w-full animate-in fade-in zoom-in-95 duration-200"
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="confirmation-dialog-title"
        aria-describedby="confirmation-dialog-description"
      >
        {/* Icon and Title */}
        <div className="flex items-center gap-3 mb-4">
          <div className={`p-2 ${iconBgClass} rounded-xl`}>
            <Icon className={`w-6 h-6 ${iconTextClass}`} />
          </div>
          <h2
            id="confirmation-dialog-title"
            className="font-display text-xl italic tracking-tighter text-light-text dark:text-pure-white"
          >
            {title}
          </h2>
        </div>

        {/* Message */}
        <p
          id="confirmation-dialog-description"
          className="font-body text-light-text-secondary dark:text-silver/80 mb-6"
        >
          {message}
        </p>

        {/* Action Buttons */}
        <div className="flex gap-3">
          <button
            onClick={onCancel}
            disabled={isLoading}
            className="flex-1 py-3 px-4 border border-light-border dark:border-border rounded-xl font-mono text-sm uppercase tracking-widest text-light-text-secondary dark:text-silver hover:border-white/20 hover:bg-silver/5 transition-smooth disabled:opacity-50 disabled:cursor-not-allowed"
            data-testid="confirmation-cancel-btn"
          >
            {cancelText}
          </button>
          <button
            onClick={onConfirm}
            disabled={isLoading}
            className={`flex-1 py-3 px-4 border rounded-xl font-mono text-sm uppercase tracking-widest transition-smooth disabled:opacity-50 disabled:cursor-not-allowed ${confirmBtnClass}`}
            data-testid="confirmation-confirm-btn"
          >
            {isLoading ? (
              <span className="flex items-center justify-center gap-2">
                <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                </svg>
                Processing...
              </span>
            ) : (
              confirmText
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ConfirmationDialog;
